import { unwrapResult } from "@reduxjs/toolkit";
import { useToast } from "hooks/useToast";
import { ApplicationHistoryEntityType, IApplicationHistoryEntity } from "models/Application.model";
import React from "react";
import { FetchPaginatedApplicationHistoryArgs } from "services/application/ApplicationService.types";
import { useStoreDispatch } from "store/hooks";
import { FetchDataForPageFunc, usePaginatedData } from "hooks/usePaginatedData";
import { fetchPaginatedApplicationHistory } from "store/domain-data/application-history/applicationHistory";
import { ApplicationHistorySortCriteria } from "models/ApplicationHistorySortCriteria.model";
import { SortDirection } from "models/SortDirection.model";
import { TABLE_DEFAULT_PAGE_SIZE } from "constants/configs";

export const usePaginatedParticipantHistory = (args: {
  applicationId: number;
  participantId: number;
  sortBy: ApplicationHistorySortCriteria;
  sortDirection: SortDirection;
}) => {
  const { applicationId, participantId, sortBy, sortDirection } = args;
  const dispatch = useStoreDispatch();
  const { toastError } = useToast();

  const [isLoading, setIsLoading] = React.useState(true);

  const fetchDataFunc: FetchDataForPageFunc<IApplicationHistoryEntity> = React.useCallback(
    async (pageIndex: number) => {
      try {
        setIsLoading(true);
        const fetchPaginatedApplicationsArgs: FetchPaginatedApplicationHistoryArgs = {
          pageIndex,
          pageSize: TABLE_DEFAULT_PAGE_SIZE,
          entity: ApplicationHistoryEntityType.Participant,
          applicationId,
          entityId: participantId,
          sortBy,
          sortDirection,
        };

        const data = await dispatch(fetchPaginatedApplicationHistory(fetchPaginatedApplicationsArgs)).then(
          unwrapResult
        );
        return {
          data,
          hasNextPage: data.length === TABLE_DEFAULT_PAGE_SIZE,
        };
      } catch (e) {
        toastError(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, toastError, applicationId, participantId, sortBy, sortDirection]
  );

  const { data: participantHistory, hasNextPage, loadNextPage } = usePaginatedData<IApplicationHistoryEntity>(
    fetchDataFunc
  );

  return {
    participantHistory,
    hasNextPage,
    loadNextPage,
    isLoading,
  };
};
